import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const PRESETS = [
  { label: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
  { label: 'Outlook / Hotmail', host: 'smtp-mail.outlook.com', port: 587, secure: false },
  { label: 'Yahoo Mail', host: 'smtp.mail.yahoo.com', port: 465, secure: true },
  { label: 'Zoho Mail', host: 'smtp.zoho.com', port: 587, secure: false },
  { label: 'Custom SMTP', host: '', port: 587, secure: false },
];

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const StatusBadge = ({ ok, label }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
    {ok ? `✓ ${label}` : label}
  </span>
);

const EmailConfig = () => {
  const [form, setForm] = useState({ smtpHost: '', smtpPort: 587, smtpSecure: false, smtpUser: '', smtpPass: '', smtpFromEmail: '', smtpFromName: 'School Management' });
  const [configured, setConfigured] = useState(false);
  const [envConfigured, setEnvConfigured] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 5000); };

  useEffect(() => {
    axios.get(`${API_BASE}/settings/email-config`, { headers: headers() })
      .then(res => {
        setForm(prev => ({ ...prev, ...res.data }));
        setConfigured(res.data.configured);
        setEnvConfigured(res.data.envConfigured);
      })
      .catch(() => flash('error', 'Failed to load email configuration'))
      .finally(() => setLoading(false));
  }, []);

  const applyPreset = (preset) => {
    setForm(f => ({ ...f, smtpHost: preset.host, smtpPort: preset.port, smtpSecure: preset.secure }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.smtpHost) return flash('error', 'SMTP host is required');
    if (!form.smtpUser) return flash('error', 'SMTP username/email is required');
    if (!form.smtpPass || form.smtpPass.startsWith('••')) return flash('error', 'SMTP password is required');
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/settings/email-config`, form, { headers: headers() });
      setConfigured(true);
      flash('success', 'Email configuration saved successfully');
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) return flash('error', 'Enter an email address to send the test to');
    setTesting(true);
    try {
      const res = await axios.post(`${API_BASE}/settings/test-email`, { to: testEmail }, { headers: headers() });
      flash('success', res.data.message);
    } catch (err) {
      flash('error', err.response?.data?.error || 'Test email failed');
    } finally { setTesting(false); }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Email Configuration</h1>
              <p className="text-xs text-gray-400 mt-0.5">Configure SMTP settings for sending emails</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6 space-y-6">
          {msg.text && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {msg.text}
            </div>
          )}

          {/* Status Row */}
          <div className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${configured ? 'bg-green-100' : envConfigured ? 'bg-blue-100' : 'bg-gray-100'}`}>
              📧
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-semibold text-gray-800">SMTP Configuration</h2>
                <StatusBadge ok={configured} label={configured ? 'DB Config Active' : 'No DB Config'} />
                {envConfigured && <StatusBadge ok={true} label=".env Fallback Available" />}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {configured ? `SMTP server: ${form.smtpHost}:${form.smtpPort}` : envConfigured ? 'Using .env SMTP settings as fallback' : 'No SMTP configured. Email features will be disabled.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* SMTP Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-800 mb-2">SMTP Settings</h3>
                <p className="text-xs text-gray-400 mb-5">These settings override the .env file values. Leave password unchanged to keep the existing saved one.</p>

                {/* Quick Presets */}
                <div className="mb-5">
                  <p className="text-xs font-medium text-gray-500 mb-2">Quick Presets</p>
                  <div className="flex flex-wrap gap-2">
                    {PRESETS.map(p => (
                      <button key={p.label} type="button" onClick={() => applyPreset(p)}
                        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 transition">
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host *</label>
                      <input type="text" value={form.smtpHost} onChange={e => setForm(f => ({ ...f, smtpHost: e.target.value }))}
                        placeholder="smtp.gmail.com" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                      <input type="number" value={form.smtpPort} onChange={e => setForm(f => ({ ...f, smtpPort: +e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <div className={`relative w-10 h-5 rounded-full transition ${form.smtpSecure ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.smtpSecure ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </div>
                      <input type="checkbox" className="hidden" checked={form.smtpSecure} onChange={e => setForm(f => ({ ...f, smtpSecure: e.target.checked, smtpPort: e.target.checked ? 465 : 587 }))} />
                      <span className="text-sm font-medium text-gray-700">Use SSL/TLS (port 465)</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Username / Email *</label>
                      <input type="email" value={form.smtpUser} onChange={e => setForm(f => ({ ...f, smtpUser: e.target.value }))}
                        placeholder="your@email.com" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password / App Password *</label>
                      <div className="relative">
                        <input type={showPass ? 'text' : 'password'} value={form.smtpPass} onChange={e => setForm(f => ({ ...f, smtpPass: e.target.value }))}
                          placeholder="Leave blank to keep existing" className="w-full border rounded-lg px-3 py-2 pr-16 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">
                          {showPass ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                      <input type="email" value={form.smtpFromEmail} onChange={e => setForm(f => ({ ...f, smtpFromEmail: e.target.value }))}
                        placeholder="noreply@myschool.com" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                      <input type="text" value={form.smtpFromName} onChange={e => setForm(f => ({ ...f, smtpFromName: e.target.value }))}
                        placeholder="School Management" className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                      {saving ? 'Saving...' : 'Save SMTP Settings'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Test + Tips */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-semibold text-gray-800 mb-3">Test Email</h3>
                <p className="text-xs text-gray-500 mb-3">Send a test email to verify your SMTP connection and settings.</p>
                <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                  placeholder="recipient@example.com" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3" />
                <button onClick={handleTest} disabled={testing}
                  className="w-full bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium">
                  {testing ? 'Sending...' : '📨 Send Test Email'}
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-amber-800 mb-2">Gmail App Password</h4>
                <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Enable 2-Step Verification on your Google account</li>
                  <li>Go to Google Account → Security</li>
                  <li>Search for "App Passwords"</li>
                  <li>Create one for "Mail" + device</li>
                  <li>Use that 16-char password here</li>
                </ol>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-700">
                <strong>Priority:</strong> DB settings (configured here) override .env file values. If this is blank, the .env SMTP config is used as fallback.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;
